import { supabase } from './supabase';

// Shared session helpers so an expired login fails gracefully (clear message +
// redirect to /auth) instead of a cryptic "Token inválido" / silent failure.

const EXPIRED_FLAG = 'zb_session_expired';

// Coalesce concurrent refreshes. supabase-js rotates the refresh token on every
// refresh and also auto-refreshes in the background; if the panel ALSO refreshes
// at the same moment, the old refresh token gets spent twice and Supabase kills
// the whole session — surfacing as "Sesión caducada" on admin actions. Sharing a
// single in-flight refresh promise prevents that race.
let pendingRefresh = null;
function sharedRefresh() {
  if (!pendingRefresh) {
    pendingRefresh = supabase.auth.refreshSession()
      .then(({ data, error }) => (error ? null : (data?.session || null)))
      .catch(() => null)
      .finally(() => { pendingRefresh = null; });
  }
  return pendingRefresh;
}

// Return a valid access token, refreshing first if it's missing or already
// expired. Returns null only when the session is truly dead (caller triggers
// re-auth).
//
// We only refresh here when the token is ALREADY past expiry — supabase-js's
// own autoRefreshToken already refreshes proactively in the background on its
// internal schedule. Doing our own preemptive refresh too (e.g. at a "60s
// left" threshold) raced that background refresh: both would spend the same
// rotating refresh token within the project's 10s reuse window, Supabase
// would reject the second one, and a perfectly healthy session — sometimes
// seconds after a fresh login — got killed and bounced to /auth with
// "sesión caducada". Only stepping in once the SDK's own refresh has
// genuinely failed (token past its expiry) avoids that race.
export async function getFreshToken() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    const revived = await sharedRefresh();
    return revived?.access_token || null;
  }
  const msLeft = session.expires_at ? session.expires_at * 1000 - Date.now() : Infinity;
  if (msLeft <= 0) {
    const refreshed = await sharedRefresh();
    return refreshed?.access_token || null;
  }
  return session.access_token;
}

// Session is dead and can't be refreshed: sign out and bounce to the login
// page with a flag so Auth can show "tu sesión ha caducado". Safe to call
// repeatedly; it no-ops the redirect if we're already on /auth.
export async function forceReauth() {
  try { await supabase.auth.signOut(); } catch { /* ignore */ }
  try { localStorage.setItem(EXPIRED_FLAG, '1'); } catch { /* ignore */ }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
    window.location.href = '/auth';
  }
}

// True (once) if the user landed on /auth because their session expired.
export function consumeExpiredFlag() {
  try {
    if (localStorage.getItem(EXPIRED_FLAG)) {
      localStorage.removeItem(EXPIRED_FLAG);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}
